import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, RefreshCw, CheckCircle, XCircle, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import type { DeploymentSlot, User, Module, SlotBookingRequest } from '../../types';
import { getApiUrl } from '../../utils/api';

interface SlotBookingProps {
  currentUser: User;
}

interface DevopsUser {
  id: string;
  name: string;
  email: string;
}

type DevopsAvailabilityStatus = 'available' | 'leave' | 'busy';

interface DevopsAvailabilityRecord {
  userId: string;
  status: DevopsAvailabilityStatus;
  scheduledLeave?: { fromDate: string; toDate: string; reason?: string }[];
}

// Must match DeploymentSlot.getOrCreateSlotsForDate in deploymentSlot.model.cjs —
// these were previously out of sync with the backend-generated slot times, so
// the displayed windows were wrong and isSlotTimePassed() (which uses endTime)
// could mark a slot "TIME PASSED" up to 30 minutes early.
const SLOT_TIMES = [
  { slotNumber: 1, startTime: '09:00', endTime: '10:30', label: 'Morning Slot 1 (9:00 AM - 10:30 AM)' },
  { slotNumber: 2, startTime: '11:00', endTime: '13:00', label: 'Morning Slot 2 (11:00 AM - 1:00 PM)' },
  { slotNumber: 3, startTime: '14:00', endTime: '15:30', label: 'Afternoon Slot 1 (2:00 PM - 3:30 PM)' },
  { slotNumber: 4, startTime: '16:00', endTime: '17:00', label: 'Afternoon Slot 2 (4:00 PM - 5:00 PM)' },
] as const;

// YYYY-MM-DD for the date as the user sees it. Date.toISOString() is UTC, which
// is the PREVIOUS day for anyone in IST between 00:00 and 05:30 — the page said
// "today" but loaded and booked yesterday's slots.
const toLocalDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const LOAD_SLOTS_ERROR = 'Failed to load slots';

const SlotBooking: React.FC<SlotBookingProps> = ({ currentUser }) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [slots, setSlots] = useState<DeploymentSlot[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [devopsUsers, setDevopsUsers] = useState<DevopsUser[]>([]);
  const [devopsAvailability, setDevopsAvailability] = useState<DevopsAvailabilityRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<typeof SLOT_TIMES[number] | null>(null);
  const [bookingForm, setBookingForm] = useState<SlotBookingRequest>({
    date: '',
    slotNumber: 1,
    mainModuleIds: [],
    mainModuleNames: [],
    dependencyModuleIds: [],
    dependencyModuleNames: [],
    deploymentTitle: '',
    notes: '',
    assignedDevopsId: '',
    assignedDevopsName: '',
    assignedDevopsEmail: '',
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Check if user has permission
  const hasPermission = ['tl', 'pm', 'devops', 'admin'].includes(currentUser.role);

  useEffect(() => {
    if (hasPermission) {
      fetchSlots();
      fetchModules();
    }
  }, [selectedDate, hasPermission]);

  useEffect(() => {
    if (hasPermission) {
      fetchDevopsUsers();
    }
  }, [hasPermission]);

  // Bumped per fetch so a slow response for a date the user has already
  // clicked past can't overwrite the slots for the date now on screen.
  const slotsFetchSeqRef = useRef(0);

  const fetchSlots = async () => {
    const seq = ++slotsFetchSeqRef.current;
    try {
      setLoading(true);
      const dateStr = toLocalDateStr(selectedDate);
      const response = await fetch(getApiUrl(`/api/deployment-slots?date=${dateStr}`));
      if (!response.ok) throw new Error('Failed to fetch slots');
      const data = await response.json();
      if (seq !== slotsFetchSeqRef.current) return;
      setSlots(data);
      // A previous load failure shouldn't linger once loading works again
      setError(prev => (prev === LOAD_SLOTS_ERROR ? '' : prev));
    } catch (err) {
      if (seq !== slotsFetchSeqRef.current) return;
      console.error('Failed to fetch slots:', err);
      setError(LOAD_SLOTS_ERROR);
    } finally {
      if (seq === slotsFetchSeqRef.current) setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await fetch(getApiUrl('/api/modules'));
      if (!response.ok) throw new Error('Failed to fetch modules');
      const data = await response.json();
      setModules(data);
    } catch (err) {
      console.error('Failed to fetch modules:', err);
    }
  };

  const fetchDevopsUsers = async () => {
    try {
      const response = await fetch(getApiUrl('/api/users'));
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      // /api/users' own `role` field is just roles[0], which can land on a
      // default Keycloak realm role instead of the real one — check the
      // full roles array first, same as SubmitRequestForm's devops filter.
      const devops = (Array.isArray(data) ? data : []).filter((u: any) => {
        const roles = u.realmRoles || u.roles || [];
        return roles.some((r: string) => r.toLowerCase() === 'devops') || u.role === 'devops';
      });
      setDevopsUsers(devops.map((u: any) => ({ id: u.id, name: u.name || u.username, email: u.email })));
    } catch (err) {
      console.error('Failed to fetch devops users:', err);
    }
  };

  // Re-fetched each time the booking modal opens so the dropdown reflects
  // whoever's status was most recently changed, not a stale page-load snapshot.
  const fetchDevopsAvailability = async () => {
    try {
      const response = await fetch(getApiUrl('/api/devops-availability'));
      if (!response.ok) throw new Error('Failed to fetch devops availability');
      const data = await response.json();
      setDevopsAvailability(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch devops availability:', err);
    }
  };

  // Checks availability for the SLOT'S date, not just "right now" — a future
  // scheduled leave (e.g. "on leave tomorrow") only disables the dropdown for
  // dates inside that range, so today's booking isn't affected by it.
  const getDevopsStatus = (userId: string, dateStr: string): DevopsAvailabilityStatus => {
    const record = devopsAvailability.find(a => a.userId === userId);
    if (!record) return 'available';
    const onScheduledLeave = (record.scheduledLeave || []).some(p => dateStr >= p.fromDate && dateStr <= p.toDate);
    if (onScheduledLeave) return 'leave';
    // The live "right now" status only describes today — applying it to every
    // date (the previous behavior) meant marking yourself Busy today also
    // blocked you from being assigned to slots weeks in the future.
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    if (dateStr === todayStr) {
      return record.status || 'available';
    }
    return 'available';
  };

  const getSlotForTime = (slotNumber: number): DeploymentSlot | undefined => {
    return slots.find(s => s.slotNumber === slotNumber);
  };

  const handleBookSlot = (slot: typeof SLOT_TIMES[number]) => {
    const dateStr = toLocalDateStr(selectedDate);
    setSelectedSlot(slot);
    setBookingForm({
      date: dateStr,
      slotNumber: slot.slotNumber as 1 | 2 | 3 | 4,
      mainModuleIds: [],
      mainModuleNames: [],
      dependencyModuleIds: [],
      dependencyModuleNames: [],
      deploymentTitle: '',
      notes: '',
      assignedDevopsId: '',
      assignedDevopsName: '',
      assignedDevopsEmail: '',
    });
    setShowBookingModal(true);
    setError('');
    setSuccess('');
    fetchDevopsAvailability();
  };

  const handleAssignedDevopsChange = (userId: string) => {
    const devops = devopsUsers.find(d => d.id === userId);
    setBookingForm({
      ...bookingForm,
      assignedDevopsId: userId,
      assignedDevopsName: devops?.name || '',
      assignedDevopsEmail: devops?.email || '',
    });
  };

  const handleMainModulesChange = (moduleIds: string[]) => {
    const selectedModules = modules.filter(m => moduleIds.includes(m._id || m.id || ''));
    setBookingForm({
      ...bookingForm,
      mainModuleIds: moduleIds,
      mainModuleNames: selectedModules.map(m => m.name),
    });
  };

  const handleDependencyModulesChange = (moduleIds: string[]) => {
    const selectedModules = modules.filter(m => moduleIds.includes(m._id || m.id || ''));
    setBookingForm({
      ...bookingForm,
      dependencyModuleIds: moduleIds,
      dependencyModuleNames: selectedModules.map(m => m.name),
    });
  };

  // Get list of already booked main module IDs for the current date
  const getBookedMainModuleIds = (): string[] => {
    const bookedIds: string[] = [];
    slots.forEach(slot => {
      if (slot.status === 'booked' || slot.status === 'in-progress') {
        if (slot.mainModuleIds && slot.mainModuleIds.length > 0) {
          bookedIds.push(...slot.mainModuleIds);
        } else if (slot.mainModuleId) {
          bookedIds.push(slot.mainModuleId);
        } else if (slot.moduleId) {
          // Legacy support
          bookedIds.push(slot.moduleId);
        }
      }
    });
    return bookedIds;
  };

  // Check if slot time has passed for today
  const isSlotTimePassed = (slotEndTime: string): boolean => {
    if (!isToday) return false; // Only check for today
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    
    // Parse slot end time (format: "HH:mm")
    const [endHour, endMinute] = slotEndTime.split(':').map(Number);
    const slotEndTimeInMinutes = endHour * 60 + endMinute;
    
    // If current time is past the slot end time, the slot has passed
    return currentTimeInMinutes > slotEndTimeInMinutes;
  };

  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (submitting || loading) {
      return;
    }
    
    setError('');
    setSuccess('');

    if (bookingForm.mainModuleIds.length === 0 || !bookingForm.deploymentTitle) {
      setError('Please select at least one main module and enter deployment title');
      return;
    }

    if (devopsUsers.length > 0 && !bookingForm.assignedDevopsId) {
      setError('Please assign a DevOps engineer to this slot');
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      
      const response = await fetch(getApiUrl('/api/deployment-slots/book'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...bookingForm,
          userId: currentUser.id,
          userName: currentUser.name,
          userEmail: currentUser.email,
          userRole: currentUser.role,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        // the server replies { error } — reading .message meant the real reason
        // (module conflict, DevOps on leave, ...) never reached the user
        throw new Error(errorData.error || errorData.message || 'Failed to book slot');
      }

      setSuccess('Slot booked successfully!');
      setShowBookingModal(false);
      fetchSlots(); // Refresh slots
      
      // Reset form
      setBookingForm({
        date: '',
        slotNumber: 1,
        mainModuleIds: [],
        mainModuleNames: [],
        dependencyModuleIds: [],
        dependencyModuleNames: [],
        deploymentTitle: '',
        notes: '',
        assignedDevopsId: '',
        assignedDevopsName: '',
        assignedDevopsEmail: '',
      });

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to book slot');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCancelSlot = async (slotId: string) => {
    // Prevent duplicate clicks
    if (submitting || loading) {
      return;
    }
    
    if (!confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      
      const response = await fetch(getApiUrl(`/api/deployment-slots/${slotId}/cancel`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userId: currentUser.id,
          userRole: currentUser.role 
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel booking');
      }
      
      setSuccess('Booking cancelled successfully!');
      fetchSlots();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel booking');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCompleteSlot = async (slotId: string) => {
    // Prevent duplicate clicks
    if (submitting || loading) {
      return;
    }
    
    if (!confirm('Mark this deployment slot as completed?')) {
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      
      const response = await fetch(getApiUrl(`/api/deployment-slots/${slotId}/complete`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to complete slot');
      }
      
      setSuccess('Slot marked as completed!');
      fetchSlots();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to complete slot');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleRejectSlot = async (slotId: string) => {
    // Prevent duplicate clicks
    if (submitting || loading) {
      return;
    }
    
    const reason = prompt('Please enter rejection reason:');
    if (!reason || reason.trim() === '') {
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      
      const response = await fetch(getApiUrl(`/api/deployment-slots/${slotId}/reject`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userId: currentUser.id,
          userRole: currentUser.role,
          reason: reason.trim()
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reject slot');
      }
      
      setSuccess('Slot rejected successfully!');
      fetchSlots();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reject slot');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const changeDate = (days: number) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const isToday = selectedDate.toDateString() === new Date().toDateString();
  const isPast = selectedDate < new Date(new Date().setHours(0, 0, 0, 0));

  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">Access Restricted</h3>
          <p className="text-yellow-700">
            Only TL, PM, DevOps, and Admin roles can access the slot booking system.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 dark:text-white">
            <Calendar className="w-8 h-8 text-blue-600" />
            Deployment Slot Booking
          </h1>
          <p className="text-gray-600 mt-2 dark:text-gray-400">
            Book deployment slots in advance — 9:00–10:30, 11:00–1:00, 2:00–3:30 and 4:00–5:00. A module can only be booked once per day.
          </p>
        </div>
        <button
          onClick={fetchSlots}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 dark:bg-gray-800 dark:border-gray-600 dark:hover:bg-gray-700 dark:bg-gray-700 dark:text-white"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 dark:bg-green-900/20 dark:border-green-800">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800 font-medium">{success}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 dark:bg-red-900/20 dark:border-red-800">
          <XCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      {/* Date Navigation */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <button
            onClick={() => changeDate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors dark:hover:bg-gray-700"
            title="Previous day"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>

          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {selectedDate.toLocaleDateString('en-GB', { 
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
            {isToday && (
              <span className="inline-block mt-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full dark:text-blue-300">
                Today
              </span>
            )}
            {isPast && !isToday && (
              <span className="inline-block mt-2 px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full dark:bg-gray-700 dark:text-gray-300">
                Past Date
              </span>
            )}
            {!isToday && !isPast && (
              <button
                onClick={goToToday}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Go to Today
              </button>
            )}
          </div>

          <button
            onClick={() => changeDate(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors dark:hover:bg-gray-700"
            title="Next day"
          >
            <ChevronRight className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Slots Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {SLOT_TIMES.map((slotTime) => {
          const slot = getSlotForTime(slotTime.slotNumber);
          const isBooked = slot && slot.status === 'booked';
          const isInProgress = slot && slot.status === 'in-progress';
          const isCompleted = slot && slot.status === 'completed';
          const isCancelled = slot && slot.status === 'cancelled';
          const isMyBooking = slot?.bookedBy?.id === currentUser.id;
          const slotTimePassed = isSlotTimePassed(slotTime.endTime);
          const canBook = !isPast && !isBooked && !isInProgress && !isCompleted && !slotTimePassed;

          return (
            <div
              key={slotTime.slotNumber}
              className={`border-2 rounded-lg p-6 transition-all ${
                slotTimePassed && !isBooked && !isInProgress && !isCompleted
                  ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                  : isBooked
                  ? 'bg-blue-50 border-blue-300'
                  : isInProgress
                  ? 'bg-yellow-50 border-yellow-300'
                  : isCompleted
                  ? 'bg-green-50 border-green-300'
                  : isCancelled
                  ? 'bg-gray-50 border-gray-300'
                  : 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md'
              }`}
            >
              {/* Slot Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className={`w-5 h-5 ${
                      isBooked || isInProgress ? 'text-blue-600' : 
                      isCompleted ? 'text-green-600' : 
                      'text-gray-400'
                    }`} />
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Slot {slotTime.slotNumber}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {slotTime.startTime} - {slotTime.endTime}
                  </p>
                </div>

                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isBooked
                    ? 'bg-blue-100 text-blue-700'
                    : isInProgress
                    ? 'bg-yellow-100 text-yellow-700'
                    : isCompleted
                    ? 'bg-green-100 text-green-700'
                    : isCancelled
                    ? 'bg-gray-100 text-gray-700'
                    : slotTimePassed
                    ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {isBooked && 'BOOKED'}
                  {isInProgress && 'IN PROGRESS'}
                  {isCompleted && 'COMPLETED'}
                  {isCancelled && 'CANCELLED'}
                  {slotTimePassed && !isBooked && !isInProgress && !isCompleted && !isCancelled && 'TIME PASSED'}
                  {!isBooked && !isInProgress && !isCompleted && !isCancelled && !slotTimePassed && 'AVAILABLE'}
                </span>
              </div>

              {/* Slot Details */}
              {slot && (isBooked || isInProgress || isCompleted || isCancelled) && (
                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium text-gray-600 min-w-[100px] dark:text-gray-400">Main Modules:</span>
                    <div className="flex flex-wrap gap-1">
                      {slot.mainModules && slot.mainModules.length > 0 ? (
                        slot.mainModules.map((mod, idx) => (
                          <span key={idx} className={`text-xs px-2 py-1 rounded font-semibold ${
                            isCancelled ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {mod}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-900 font-semibold dark:text-white">
                          {slot.mainModule || slot.module}
                        </span>
                      )}
                    </div>
                  </div>
                  {slot.dependencyModules && slot.dependencyModules.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[100px] dark:text-gray-400">Dependencies:</span>
                      <div className="flex flex-wrap gap-1">
                        {slot.dependencyModules.map((dep, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-300">
                            {dep}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium text-gray-600 min-w-[100px] dark:text-gray-400">Deployment:</span>
                    <span className="text-sm text-gray-900 dark:text-white">{slot.deploymentTitle}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium text-gray-600 min-w-[100px] dark:text-gray-400">Booked by:</span>
                    <span className="text-sm text-gray-900 dark:text-white">
                      {slot.bookedBy?.name} ({slot.bookedBy?.role.toUpperCase()})
                    </span>
                  </div>
                  {slot.assignedDevops?.name && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[100px] dark:text-gray-400">Assigned to:</span>
                      <span className="text-sm text-gray-900 dark:text-white">{slot.assignedDevops.name}</span>
                    </div>
                  )}
                  {slot.notes && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[100px] dark:text-gray-400">Notes:</span>
                      <span className="text-sm text-gray-700 italic dark:text-gray-300">{slot.notes}</span>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium text-gray-600 min-w-[100px] dark:text-gray-400">Booked at:</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {slot.bookedAt && new Date(slot.bookedAt).toLocaleString('en-GB')}
                    </span>
                  </div>
                </div>
              )}

              {/* Show rejection details if cancelled */}
              {slot && isCancelled && (slot as any).rejectionReason && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg dark:bg-orange-900/20 dark:border-orange-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-orange-900 dark:text-orange-200">Rejected by DevOps</p>
                      <p className="text-sm text-orange-700 mt-1 dark:text-orange-300">{(slot as any).rejectionReason}</p>
                      {(slot as any).rejectedAt && (
                        <p className="text-xs text-orange-600 mt-1">
                          {new Date((slot as any).rejectedAt).toLocaleString('en-GB')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                {canBook && (
                  <button
                    onClick={() => handleBookSlot(slotTime)}
                    disabled={loading || submitting}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {submitting ? 'Booking...' : 'Book This Slot'}
                  </button>
                )}

                {isMyBooking && isBooked && (
                  <>
                    <button
                      onClick={() => handleCompleteSlot(slot.id || slot._id || '')}
                      disabled={loading || submitting}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {submitting ? 'Processing...' : 'Mark Complete'}
                    </button>
                    <button
                      onClick={() => handleCancelSlot(slot.id || slot._id || '')}
                      disabled={loading || submitting}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {submitting ? 'Processing...' : 'Cancel'}
                    </button>
                  </>
                )}

                {currentUser.role === 'devops' && isBooked && !isMyBooking && (
                  <>
                    <button
                      onClick={() => handleCompleteSlot(slot.id || slot._id || '')}
                      disabled={loading || submitting}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {submitting ? 'Processing...' : 'Mark Complete'}
                    </button>
                    <button
                      onClick={() => handleRejectSlot(slot.id || slot._id || '')}
                      disabled={loading || submitting}
                      className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {submitting ? 'Processing...' : 'Reject'}
                    </button>
                    <button
                      onClick={() => handleCancelSlot(slot.id || slot._id || '')}
                      disabled={loading || submitting}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {submitting ? 'Processing...' : 'Cancel'}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto dark:bg-gray-800">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Book Deployment Slot</h2>
              <p className="text-gray-600 mt-2 dark:text-gray-400">
                {selectedSlot.label} on {selectedDate.toLocaleDateString('en-GB')}
              </p>
            </div>

            <form onSubmit={handleSubmitBooking} className="p-6 space-y-4">
              {/* Main Modules Selection (Multiple) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                  Main Modules <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-300 rounded-lg p-3 max-h-60 overflow-y-auto space-y-2 dark:border-gray-600">
                  {modules
                    .filter(m => {
                      const moduleId = m._id || m.id || '';
                      const bookedIds = getBookedMainModuleIds();
                      // Don't show modules already booked today
                      return !bookedIds.includes(moduleId);
                    })
                    .map((module) => (
                      <label key={module._id || module.id} className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 p-2 rounded border border-transparent hover:border-blue-200">
                        <input
                          type="checkbox"
                          checked={bookingForm.mainModuleIds.includes(module._id || module.id || '')}
                          onChange={(e) => {
                            const moduleId = module._id || module.id || '';
                            if (e.target.checked) {
                              handleMainModulesChange([...bookingForm.mainModuleIds, moduleId]);
                            } else {
                              handleMainModulesChange(bookingForm.mainModuleIds.filter(id => id !== moduleId));
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 font-medium dark:text-gray-300">{module.name}</span>
                      </label>
                    ))}
                </div>
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                  Select one or more main modules for this deployment. Already booked modules are hidden.
                </p>
                {bookingForm.mainModuleIds.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="text-xs text-gray-600 dark:text-gray-400">Selected:</span>
                    {bookingForm.mainModuleNames.map((name, idx) => (
                      <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold dark:text-blue-300">
                        {name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Dependency Modules Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                  Dependency Modules (Optional)
                </label>
                <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2 dark:border-gray-600">
                  {modules
                    .filter(m => !bookingForm.mainModuleIds.includes(m._id || m.id || ''))
                    .map((module) => (
                      <label key={module._id || module.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded dark:hover:bg-gray-700">
                        <input
                          type="checkbox"
                          checked={bookingForm.dependencyModuleIds.includes(module._id || module.id || '')}
                          onChange={(e) => {
                            const moduleId = module._id || module.id || '';
                            if (e.target.checked) {
                              handleDependencyModulesChange([...bookingForm.dependencyModuleIds, moduleId]);
                            } else {
                              handleDependencyModulesChange(bookingForm.dependencyModuleIds.filter(id => id !== moduleId));
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{module.name}</span>
                      </label>
                    ))}
                </div>
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                  Select additional modules that this deployment depends on
                </p>
              </div>

              {/* Assign DevOps Engineer */}
              {devopsUsers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                    Assign DevOps Engineer <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={bookingForm.assignedDevopsId || ''}
                    onChange={(e) => handleAssignedDevopsChange(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">-- Select a DevOps engineer --</option>
                    {devopsUsers.map(d => {
                      const status = getDevopsStatus(d.id, bookingForm.date);
                      const unavailable = status !== 'available';
                      return (
                        <option key={d.id} value={d.id} disabled={unavailable}>
                          {d.name}{status === 'leave' ? ' (On Leave)' : status === 'busy' ? ' (Busy)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                    Only engineers marked "Available" can be assigned. Someone on leave or busy shows disabled.
                  </p>
                </div>
              )}

              {/* Deployment Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                  Deployment Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={bookingForm.deploymentTitle}
                  onChange={(e) => setBookingForm({ ...bookingForm, deploymentTitle: e.target.value })}
                  required
                  placeholder="e.g., Bug Fix - User Login Issue"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                  Notes (Optional)
                </label>
                <textarea
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                  placeholder="Add any additional notes or requirements"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBookingModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:bg-gray-700 dark:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || submitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {submitting || loading ? 'Booking...' : 'Confirm Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 dark:bg-blue-900/20 dark:border-blue-800">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2 dark:text-blue-200">
          <AlertCircle className="w-5 h-5" />
          Booking Rules
        </h3>
        <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>There are 4 deployment slots per day: 9:00–10:30, 11:00–1:00, 2:00–3:30 and 4:00–5:00</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>You can select multiple main modules for each deployment slot</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>Once a module is booked in any slot, it cannot be booked in another slot on the same day</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>Already booked modules are automatically hidden when booking a new slot</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>All modules become available again the next day</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>You can also select multiple dependency modules for each deployment</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>TL, PM, and DevOps can book slots in advance</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>You can cancel your own bookings or mark them as complete</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>Admin and DevOps can cancel any booking and mark any slot as complete</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SlotBooking;
